import React from "react";
import { Pencil, MoveRight, MessageSquare, FilePlus, CheckSquare, StickyNote } from "lucide-react";
import { Avatar, Typography, Box, Card, CardContent, Divider } from "@mui/joy";
import moment from "moment";

import "./index.scss";

export type TimelineEvent = {
    id: string;
    type: "comment" | "update" | "move" | "note" | "task" | "status-change" | "document" | "milestone";
    user: {
        name: string;
        avatarUrl: string;
    };
    date: string;
    details: string; // Could be something like "Changed status to In Progress"
    icon?: React.ReactNode;
    title?: string;
};

type TimelineProps = {
    events: TimelineEvent[];
};

const Timeline: React.FC<TimelineProps> = ({ events }) => {
    const getIcon = (type: TimelineEvent["type"]) => {
        switch (type) {
            case "comment":
                return <MessageSquare size={16} />;
            case "update":
                return <Pencil size={16} />;
            case "move":
                return <MoveRight size={16} />;
            case "note":
                return <StickyNote size={16} />;
            case "task":
                return <CheckSquare size={16} />;
            case "status-change":
                return <MoveRight size={16} />;
            case "document":
                return <FilePlus size={16} />;
            default:
                return <MessageSquare size={16} />;
        }
    };

    const getDescription = (type: TimelineEvent["type"], details: string) => {
        switch (type) {
            case "note":
                return `added a note:`;
            case "task":
                return `created a task: `;
            case "status-change":
                return `updated status to "${details}"`;
            case "document":
                return `uploaded document`;
            case "comment":
                return `commented:`;
            case "update":
                return `made an update: "${details}"`;
            case "move":
                return `moved task to "${details}"`;
            case "milestone":
                return ""; // Hide generic description for milestones
            default:
                return details;
        }
    };

    const getDetails = (type: TimelineEvent["type"], details: string) => {
        switch (type) {
            case "note":
                return <Notes details={details} />;
            case "document":
                return <DocumentPreview filename={details} />;
            default:
                return null;
        }
    };

    return (
        <ol className="timeline">
            {events.map((event, idx) => (
                <li key={event.id} className={`timeline-item ${idx === events.length - 1 ? "extra-space" : ""}`}>
                    <span className="timeline-item-icon faded-icon">{event.icon || getIcon(event.type)}</span>
                    <div className="timeline-item-description">
                        <Avatar
                            src={event.user.avatarUrl}
                            alt={event.user.name}
                            size="sm"
                            variant="solid"
                            color={
                                event.type === "status-change"
                                    ? "success"
                                      : event.type === "note"
                                      ? "warning"
                                      : event.type === "task"
                                        ? "primary"
                                        : event.type === "document"
                                          ? "danger"
                                          : event.type === "comment"
                                            ? "warning"
                                            : event.type === "update"
                                              ? "success"
                                              : event.type === "milestone"
                                              ? "primary"
                                              : "warning"
                            }
                        />

                        <Box>
                            <Box display={"flex"} alignItems="center" mt={0.5} gap={1} flexWrap="wrap">
                                {event.type !== "milestone" && <Typography sx={{ fontWeight: "lg" }}>{event.user.name}</Typography>}
                                <Typography sx={{ fontWeight: event.type === "milestone" ? "lg" : "md", color: "text.primary" }}>
                                    {event.title || getDescription(event.type, event.details)}
                                </Typography>
                                <Typography sx={{ color: "text.tertiary" }} fontSize="sm">
                                    • {moment(event.date).format("MMM DD, YYYY")}
                                </Typography>
                            </Box>
                            {event.type === "milestone" ? (
                                <Typography level="body-sm" sx={{ mt: 0.5, opacity: 0.8 }}>
                                    {event.details}
                                </Typography>
                            ) : (
                                getDetails(event.type, event.details)
                            )}
                        </Box>
                    </div>
                </li>
            ))}
        </ol>
    );
};

function Notes(props: { details: string }) {
    const { details } = { ...props };
    return (
        <Card sx={{ mt: 1, p: 1, px: 1.5 }}>
            <CardContent>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Divider
                        orientation="vertical"
                        sx={(theme: any) => ({
                            "--Divider-thickness": "4px",
                            borderRadius: 4,
                            backgroundColor: "success.main", // MUI palette key
                        })}
                    />
                    <Typography sx={{ color: "text.primary" }}>{details}</Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

function DocumentPreview(props: { filename: string }) {
    const { filename } = props;
    const isLink = filename.startsWith("http") || filename.includes(".pdf") || filename.includes(".doc");

    return (
        <Card sx={{ mt: 1, p: 1, px: 1.5 }}>
            <CardContent>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Divider
                        orientation="vertical"
                        sx={{
                            width: "4px",
                            borderRadius: "4px",
                            backgroundColor: "primary.main",
                        }}
                    />
                    <Typography
                        component="a"
                        href={isLink ? filename : undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: "primary.solidBg",
                            textDecoration: isLink ? "underline" : "none",
                            cursor: isLink ? "pointer" : "default",
                        }}
                    >
                        {filename}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

export default Timeline;
